import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import * as moment from "moment";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextInput,
  Dropdown,
  RadioButton,
  DropdownWithSearch,
} from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import { loadSingleCenterProduct } from "../CenterProductsList/CenterProductsList.actionCreators";
import MaterialUsage from "./components/MaterialUsage/MaterialUsage";
import Header from "../../Company/CompanyView/components/Header/Header";
import {setSingleCenterProductNotFound} from "../CenterProductsList/CenterProductsList.slice";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
  RequiredMessage,
} from "@utils/extra-style";
import UserInfo from "@utils/UserInfo";
import { getValorisationRateOptions } from "@utils/utils";

import "@utils/extra-style.scss";

import {
  ButtonWrapper,
  FormBody,
  SectionContainer,
  OuterWrapper,
  FlexRow,
  ContainerLabel,
  GridRow,
  OuterWrapperBackground,
  PaddingDiv,
} from "./AddEditCenterProduct.styled";
import "./AddEditCenterProduct.scss";
import NotFound from "@components/Errors/NotFound";

const AddEditCenterProduct = (props) => {
  const { t, id, isEditMode } = props;

  const history = useNavigate();
  const params = useParams();
  const location = useLocation();
  const {  path, centerId, productId, type } = params;

  const isValidEditUrl =
    path === "edit" &&
    /^\d+$/.test(params.id) &&
    /^\d+$/.test(centerId) &&
    /^\d+$/.test(productId);

  const isValidCloneUrl =
    path === "clone" &&
    /^\d+$/.test(params.id) &&
    /^\d+$/.test(centerId) &&
    /^\d+$/.test(productId) &&
    ["1", "2"].includes(type);

  const isValidCreateUrl =
    path === "create" && /^\d+$/.test(params.id) && /^\d+$/.test(centerId);

  const isSingleCenterProductNotFound = useSelector(
    (state) => state.centerProductsList.isSingleCenterProductNotFound
  );

  const isValidUrl = isValidEditUrl || isValidCloneUrl || isValidCreateUrl;

  const [subCategoryList, setSubCategoryList] = useState([]);
  const [materialSubCategoryList, setMaterialSubCategoryList] = useState([]);
  const [isMaterial, setIsMaterial] = useState(false);
  const [productList, setProductList] = useState([]);
  const [isHaulier, setIsHaulier] = useState(false);
  const [transportList, setTransportList] = useState([]);
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  const [materialUsageList, setMaterialUsageList] = useState([]);
  const [createCenterProductAPIError, setCreateCenterProductAPIError] =
    useState("");
  const [isCenterProductCreating, setIsCenterProductCreating] = useState(false);

  // Determine action mode from URL path and isEditMode prop
  const getActionMode = () => {
    if (isEditMode) return "edit";
    if (params.path === "clone") return "clone";
    return "add";
  };
  const actionMode = getActionMode();
  // Get IDs from URL params
  // Routes: /company/view/:id/product/edit/:productId, /company/view/:id/product/:path/:id/:type, /company/view/:id/product/:path
  const companyId = params.id;
  const centerProductId = productId || "";

  const countryId = UserInfo.countryId();
  const dispatch = useDispatch();

  const selectedCenterProduct = useSelector(
    (state) => state.centerProductsList.selectedCenterProduct
  );
  // Get centerId from selectedCenterProduct
  const getCenterName = () =>
    selectedCenterProduct?.center?.name || companyData?.centre?.name || "";
 
  const fetchCenterProductInfo = () => {
    if (!companyId || !centerId) return;
    dispatch(
      loadSingleCenterProduct({
        centerProductId,
        centerId,
        companyId,
      })
    );
  };

  useEffect(() => {
    dispatch(setSingleCenterProductNotFound(false));
    if (actionMode === "edit" || actionMode === "clone") {
      if (centerId) {
        fetchCenterProductInfo();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerId]);

  const [formData, setFormData] = useState({
    label: {
      required: false,
      value: selectedCenterProduct?.label ?? "",
      error: "",
    },
    logisticFlow: {
      required: false,
      value: selectedCenterProduct?.logisticFlow ?? "",
      error: "",
    },
    subCategory: {
      required: false,
      value: selectedCenterProduct?.subCategory?.id ?? "",
      error: "",
    },
    product: {
      required: false,
      value: selectedCenterProduct?.product?.id ?? "",
      error: "",
    },
    serviceType: {
      required: false,
      value: selectedCenterProduct?.serviceType ?? "",
      error: "",
    },
    haulier: {
      required: false,
      value: selectedCenterProduct?.haulier?.id ?? "",
      error: "",
    },
    acceptanceRequirements: {
      required: false,
      value: selectedCenterProduct?.acceptanceRequirements ?? "",
      error: "",
    },
    comment: {
      required: true,
      value: selectedCenterProduct?.comment ?? "",
      error: "",
    },
    valorisationRate: {
      required: false,
      value: selectedCenterProduct?.valorisationRate ?? "",
      error: "",
    },
    treatmentType: {
      value: selectedCenterProduct?.valorisationTreatmentId ?? "",
      error: "",
      required: true,
    },
    dailyMaxQuantity: {
      value: selectedCenterProduct?.dailyMaxQuantity ?? "",
      error: "",
      required: true,
    },
    annualMaxQuantity: {
      value: selectedCenterProduct?.annualMaxQuantity ?? "",
      error: "",
      required: true,
    },
    residualQuantity: {
      value: selectedCenterProduct?.residualQuantity ?? "",
      error: "",
      required: true,
    },
    minBilling: {
      value: selectedCenterProduct?.minBilling ?? "",
      error: "",
      required: true,
    },
    materialUsages: {
      value: selectedCenterProduct?.materialUsages ?? "",
      error: "",
      required: true,
    },
  });

  const [acceptanceRequirements, setAcceptanceRequirements] = useState(
    selectedCenterProduct?.acceptanceRequirements ?? ""
  );

  const [comment, setComment] = useState(
    selectedCenterProduct?.internalComment ?? ""
  );

  const companyIdParsed = parseInt(companyId, 10);
  const [companyData, setCompanyData] = useState([]);
  const companyInfo = useSelector((state) => state.companyList.byId[companyId]);

  const selectedCompanyData = useSelector(
    (state) => state.companyList.selectedCompany
  );

  useEffect(() => {
    if (
      companyInfo !== undefined &&
      companyIdParsed === parseInt(companyInfo.id, 10)
    ) {
      setCompanyData(companyInfo);
    }
    if (
      selectedCompanyData !== null &&
      companyIdParsed === parseInt(selectedCompanyData.id, 10)
    ) {
      setCompanyData(selectedCompanyData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo, selectedCompanyData]);

  useEffect(() => {
    if (selectedCenterProduct?.id) {
      let selectedMaterialArr = "";
      if (selectedCenterProduct?.materialUsages) {
        selectedMaterialArr = selectedCenterProduct.materialUsages.map(
          (material) => {
            return material.id;
          }
        );
      }
      setFormData({
        label: {
          required: false,
          value: selectedCenterProduct?.label ?? "",
          error: "",
        },
        logisticFlow: {
          required: false,
          value: selectedCenterProduct?.logisticFlow ?? "",
          error: "",
        },
        subCategory: {
          required: false,
          value: selectedCenterProduct?.product?.subCategory?.id ?? "",
          error: "",
        },
        product: {
          required: false,
          value: {
            value: selectedCenterProduct?.product?.id,
            label: selectedCenterProduct?.product?.name ?? "",
          },
          error: "",
        },
        serviceType: {
          required: false,
          value: selectedCenterProduct?.serviceType ?? "",
          error: "",
        },
        haulier: {
          required: false,
          value: {
            value: selectedCenterProduct?.haulier?.id ?? "",
            label: selectedCenterProduct?.haulier?.id
              ? selectedCenterProduct?.haulier?.companyId +
                " - " +
                selectedCenterProduct?.haulier?.companyName
              : "",
          },
          error: "",
        },
        acceptanceRequirements: {
          required: false,
          value: selectedCenterProduct?.acceptanceRequirements ?? "",
          error: "",
        },
        comment: {
          required: true,
          value: selectedCenterProduct?.internalComment ?? "",
          error: "",
        },
        valorisationRate: {
          required: false,
          value: selectedCenterProduct?.valorisationRate ?? "",
          error: "",
        },
        treatmentType: {
          value: selectedCenterProduct?.valorisationTreatmentId ?? "",
          error: "",
          required: true,
        },
        dailyMaxQuantity: {
          value: selectedCenterProduct?.dailyMaxQuantity ?? "",
          error: "",
          required: true,
        },
        annualMaxQuantity: {
          value: selectedCenterProduct?.annualMaxQuantity ?? "",
          error: "",
          required: true,
        },
        residualQuantity: {
          value: selectedCenterProduct?.residualCapacity ?? "",
          error: "",
          required: true,
        },
        minBilling: {
          value: selectedCenterProduct?.minimumBilling ?? "",
          error: "",
          required: true,
        },
        materialUsages: {
          value: selectedMaterialArr ? selectedMaterialArr.toString() : "",
          error: "",
          required: true,
        },
      });
      if (selectedCenterProduct?.logisticFlow === 2) {
        setIsHaulier(true);
      }
      setAcceptanceRequirements(selectedCenterProduct?.acceptanceRequirements);
      setComment(selectedCenterProduct?.internalComment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCenterProduct]);

  useEffect(() => {
    setFormData({
      ...formData,
      acceptanceRequirements: {
        value: acceptanceRequirements,
        error: "",
        required: true,
      },
    });
  }, [acceptanceRequirements]);

  useEffect(() => {
    setFormData({
      ...formData,
      comment: {
        value: comment,
        error: "",
        required: true,
      },
    });
  }, [comment]);

  const onLabelChange = (e) => {
    setFormData({
      ...formData,
      label: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onLogisticFlowSelect = (e) => {
    setFormData({
      ...formData,
      logisticFlow: {
        value: parseInt(e.target.value, 10),
        error: "",
        required: true,
      },
    });
    if (parseInt(e.target.value, 10) === 2) {
      setIsHaulier(true);
    } else {
      setIsHaulier(false);
    }
  };

  const onSubCategoryChange = (e) => {
    const subCategoryId = e.value;
    setFormData({
      ...formData,
      subCategory: {
        value: subCategoryId,
        error: "",
        required: true,
      },
      product: {
        value: "",
        error: "",
        required: true,
      },
    });

    if (materialSubCategoryList.indexOf(subCategoryId) !== -1) {
      setIsMaterial(true);
    } else {
      setIsMaterial(false);
    }
  };

  const onProductChange = (filter) => {
    setFormData({
      ...formData,
      product: {
        value: filter,
        error: "",
        required: true,
      },
    });
  };

  const onServiceTypeSelect = (e) => {
    setFormData({
      ...formData,
      serviceType: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onHaulierChange = (filter) => {
    setFormData({
      ...formData,
      haulier: {
        value: filter,
        error: "",
        required: true,
      },
    });
  };

  const onValorisationRateChange = (e) => {
    setFormData({
      ...formData,
      valorisationRate: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onTreatmentTypeChange = (e) => {
    setFormData({
      ...formData,
      treatmentType: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onDailyMaxQuantityChange = (e) => {
    setFormData({
      ...formData,
      dailyMaxQuantity: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onAnnualMaxQuantityChange = (e) => {
    setFormData({
      ...formData,
      annualMaxQuantity: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onResidualQuantityChange = (e) => {
    setFormData({
      ...formData,
      residualQuantity: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onMinBillingChange = (e) => {
    setFormData({
      ...formData,
      minBilling: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const fetchSubCategories = () => {
    axios
      .get("/general/subCategories")
      .then((response) => {
        const subCategoryData = response.data.subCategories.map((subCat) => {
          return {
            label: subCat.category.name + " - " + subCat.name,
            value: subCat.id,
          };
        });
        setSubCategoryList(subCategoryData);

        const materialSubCategoryData = response.data.subCategories
          .filter((subCat) => parseInt(subCat.category.id) === 2)
          .map((subCat) => subCat.id);

        setMaterialSubCategoryList(materialSubCategoryData);
      })
      .catch((error) => {});
  };

  const fetchTransport = () => {
    axios
      .get(`/companies/hauliers?countries=[${countryId}]&status=1&limit=-1`)
      .then((response) => {
        const responseData = response.data.hauliers.map((item) => {
          const tempData = {
            value: String(item.id),
            label: item.companyId + " - " + item.companyName,
          };
          return tempData;
        });
        setTransportList(responseData);
      })
      .catch((error) => {});
  };

  const fetchValorisationTreatmentTypes = () => {
    axios
      .get(`/general/treatmentTypes?countryId=${countryId}`)
      .then((response) => {
        const responseData = response.data.treatmentType.map((item) => {
          const tempData = {
            value: item.id,
            label: item.name,
          };
          return tempData;
        });
        setTreatmentTypes(responseData);
      })
      .catch((error) => {});
  };

  const fetchMaterialUages = () => {
    axios
      .get(`/general/materialUsages?countryId=${countryId}`)
      .then((response) => {
        setMaterialUsageList(response.data.materialUsage);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    fetchSubCategories();
    fetchTransport();
    fetchValorisationTreatmentTypes();
    fetchMaterialUages();
  }, []);

  const fetchProducts = (subCategory) => {
    axios
      .get(
        `/products?status=1&subCategories=[${subCategory}]&countries=${countryId}&orderBy=asc&limit=-1`
      )
      .then((response) => {
        const productData = response.data.products.map((product) => {
          return {
            label: product.name,
            value: String(product.id),
          };
        });
        setProductList(productData);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    if (formData.subCategory.value !== "")
      fetchProducts(formData.subCategory.value);
  }, [formData.subCategory.value]);

  const onCancel = () => {
    history(-1);
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.logisticFlow.value) {
      isFormValid = false;
      data.logisticFlow.error = t("FIELD_REQUIRED");
    }
    if (!data.subCategory.value) {
      isFormValid = false;
      data.subCategory.error = t("FIELD_REQUIRED");
    }
    if (!data.product.value) {
      isFormValid = false;
      data.product.error = t("FIELD_REQUIRED");
    }
    if (!data.serviceType.value) {
      isFormValid = false;
      data.serviceType.error = t("FIELD_REQUIRED");
    }
    if (!data.haulier.value && parseInt(data.logisticFlow.value, 10) === 2) {
      isFormValid = false;
      data.haulier.error = t("FIELD_REQUIRED");
    } else {
      data.haulier.error = "";
    }
    if (!data.valorisationRate.value && data.valorisationRate.value !== 0) {
      isFormValid = false;
      data.valorisationRate.error = t("FIELD_REQUIRED");
    }
    if (!data.treatmentType.value) {
      isFormValid = false;
      data.treatmentType.error = t("FIELD_REQUIRED");
    }

    if (!isFormValid) {
      setFormData({ ...data });
    }
    return isFormValid;
  };
  const getFormData = () => {
    const materialArr = [];
    if (formData.materialUsages.value !== "") {
      const materials = formData.materialUsages.value.split(",");
      materials.map((material) => {
        materialArr.push({
          id: material,
        });
      });
    }

    const data = {
      id: isEditMode ? selectedCenterProduct.id : "",
      label: formData.label.value,
      serviceType: formData.serviceType.value,
      subCategory: {
        id: formData.subCategory.value,
      },
      isActive: true,
      product: {
        id: formData.product.value.value,
      },
      haulier: {
        id: formData.haulier.value.value,
      },
      logisticFlow: formData.logisticFlow.value,
      acceptanceRequirements: formData.acceptanceRequirements.value,
      comment: formData.comment.value,
      valorisationRate: formData.valorisationRate.value,
      valorisationTreatment: formData.treatmentType.value,
      dailyMaxQuantity: formData.dailyMaxQuantity.value,
      annualMaxQuantity: formData.annualMaxQuantity.value,
      residualCapacity: formData.residualQuantity.value,
      strictLimit: true,
      minimumBilling: formData.minBilling.value,
      materialUsages: materialArr,
    };
    if (actionMode === "clone" && parseInt(params?.type, 10) === 2) {
      const activePriceList = selectedCenterProduct.prices.filter(
        (price) => price.isActive === 1
      );
      const result = activePriceList.map(({ id, ...rest }) => ({
        ...rest,
      }));
      const pricesList = { prices: result };
      Object.assign(data, pricesList);
    }
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      const data = getFormData(formData);
      setIsCenterProductCreating(true);
      setCreateCenterProductAPIError("");
      // Create and copy price
      if (actionMode === "add" || actionMode === "clone") {
        axios
          .post(`/companies/${companyId}/centres/${centerId}/products`, data)
          .then((response) => {
            const centreProductId = response.data[0].id;
            setIsCenterProductCreating(false);
            history(
              `/company/view/${companyId}/center/${centerId}/product/view/${centreProductId}`
            );
          })
          .catch((error) => {
            setIsCenterProductCreating(false);
            setCreateCenterProductAPIError(error.response?.data?.message ?? "");
          });
      }
      // Edit
      if (actionMode === "edit") {
        axios
          .put(
            `/companies/${companyId}/centres/${centerId}/products/${centerProductId}`,
            data
          )
          .then((response) => {
            setIsCenterProductCreating(false);
            history(
              `/company/view/${companyId}/center/${centerId}/product/view/${centerProductId}`
            );
          })
          .catch((error) => {
            setIsCenterProductCreating(false);
            setCreateCenterProductAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  useEffect(() => {
    if (
      (actionMode === "edit" || actionMode === "clone") &&
      !!selectedCenterProduct &&
      !!materialSubCategoryList
    ) {
      if (
        materialSubCategoryList.indexOf(
          selectedCenterProduct.product.subCategory.id
        ) !== -1
      ) {
        setIsMaterial(true);
      }
    }
  }, [selectedCenterProduct, materialSubCategoryList]);

  const onCompanyInfoClick = (id) => {
    history(`/company/view/${id}/general-info`);
  };

  if (!isValidUrl || isSingleCenterProductNotFound) {
    return <NotFound />;
  }

  return (
    <OuterWrapperBackground>
      <Header
        companyData={companyData}
        onCompanyInfoClick={onCompanyInfoClick}
      />
      <PaddingDiv>
        <FormBody>
          <OuterWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr">
                    <ContainerLabel htmlFor="container-meeting-summary">
                      {actionMode === "add" &&
                        getCenterName() + " " + t("CENTRE_PRODUCT")}
                      {actionMode === "edit" &&
                        getCenterName() +
                          " " +
                          t("CENTRE_PRODUCT") +
                          " #" +
                          centerProductId}
                    </ContainerLabel>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      type="text"
                      label={t("CENTRE_PRODUCT_LABEL")}
                      onChange={onLabelChange}
                      name="center-product-title"
                      id="center-product-title"
                      value={String(formData.label?.value) || ""}
                      isFloat
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.label.error}
                      placeholder={t("CENTRE_PRODUCT_LABEL")}
                    />
                    <div
                      className={
                        formData.logisticFlow.error === ""
                          ? "center-product__title"
                          : "center-product__title_error"
                      }
                    >
                      <label htmlFor="center-product">
                        {t("LOGISTIC_FLOW")}
                      </label>
                      <RadioButton
                        value={formData.logisticFlow.value}
                        onChange={onLogisticFlowSelect}
                        name="price-type"
                        options={[
                          {
                            label: t("LOGISTIC_FLOW_ROUND_TRIP"),
                            value: 1,
                          },
                          { label: t("LOGISTIC_FLOW_RETURN_LOAD"), value: 2 },
                        ]}
                      />

                      {formData.logisticFlow.error && (
                        <RequiredMessage margin="-20px 0 0 10px">
                          {formData.logisticFlow.error}
                        </RequiredMessage>
                      )}
                    </div>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <Dropdown
                      id="sub-category"
                      label={t("SUB_CATEGORY")}
                      name="subCategory"
                      value={formData.subCategory.value}
                      onChange={onSubCategoryChange}
                      defaultOption={t("SUB_CATEGORY")}
                      options={subCategoryList}
                      labelStyles={
                        formData.subCategory.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.subCategory.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.subCategory.error}
                    />
                    <div className="center-product__dropdown">
                      <DropdownWithSearch
                        placeholder={t("PRODUCT")}
                        label={t("PRODUCT")}
                        value={formData.product.value}
                        options={productList}
                        required
                        isMulti={false}
                        error={formData.product.error}
                        onChange={onProductChange}
                        labelStyles={
                          formData.product.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        inputClass={
                          formData.product.error !== ""
                            ? "dropdown-with-search__dropdown-input-error"
                            : "dropdown-with-search__dropdown-input"
                        }
                      />
                    </div>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <div
                      className={
                        formData.serviceType.error === ""
                          ? "center-product__title"
                          : "center-product__title_error"
                      }
                    >
                      <label htmlFor="center-product">{t("OPERATION")}</label>
                      <RadioButton
                        value={formData.serviceType.value}
                        onChange={onServiceTypeSelect}
                        name="service-type"
                        options={[
                          {
                            label: t("REMOVAL"),
                            value: "IN",
                          },
                          { label: t("SUPPLY"), value: "OUT" },
                        ]}
                      />
                      {formData.serviceType.error && (
                        <RequiredMessage margin="-20px 0 0 10px">
                          {formData.serviceType.error}
                        </RequiredMessage>
                      )}
                    </div>
                    {isHaulier && (
                      <DropdownWithSearch
                        placeholder={t("HAULIER")}
                        label={t("HAULIER")}
                        value={formData.haulier.value}
                        options={transportList}
                        required={
                          parseInt(formData.logisticFlow.value, 10) === 2
                        }
                        isMulti={false}
                        error={formData.haulier.error}
                        onChange={onHaulierChange}
                        labelStyles={
                          formData.haulier.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        inputClass={
                          formData.haulier.error !== ""
                            ? "dropdown-with-search__dropdown-input-error"
                            : "dropdown-with-search__dropdown-input"
                        }
                      />
                    )}
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <Dropdown
                      id="valorisation-rate"
                      label={t("VALORISATION_RATE")}
                      name="valorisationRate"
                      value={formData.valorisationRate.value}
                      onChange={onValorisationRateChange}
                      defaultOption={t("VALORISATION_RATE")}
                      options={getValorisationRateOptions}
                      labelStyles={
                        formData.valorisationRate.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.valorisationRate.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.valorisationRate.error}
                    />
                    <Dropdown
                      id="treatment-type"
                      label={t("TREATMENT_TYPE")}
                      name="treatmentType"
                      value={formData.treatmentType.value}
                      onChange={onTreatmentTypeChange}
                      defaultOption={t("TREATMENT_TYPE")}
                      options={treatmentTypes}
                      labelStyles={
                        formData.treatmentType.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.treatmentType.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.treatmentType.error}
                    />
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      type="text"
                      label={t("DAILY_MAX_QUANTITY")}
                      onChange={onDailyMaxQuantityChange}
                      name="center-product-dailyMaxQuantity"
                      id="center-product-dailyMaxQuantity"
                      value={String(formData.dailyMaxQuantity?.value) || ""}
                      isFloat
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.dailyMaxQuantity.error}
                      placeholder={t("DAILY_MAX_QUANTITY")}
                    />
                    <TextInput
                      type="text"
                      label={t("ANNUAL_MAX_QUANTITY")}
                      onChange={onAnnualMaxQuantityChange}
                      name="center-product-annualMaxQuantity"
                      id="center-product-annualMaxQuantity"
                      value={String(formData.annualMaxQuantity?.value) || ""}
                      isFloat
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.annualMaxQuantity.error}
                      placeholder={t("ANNUAL_MAX_QUANTITY")}
                    />
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      type="text"
                      label={t("RESIDUAL_QUANTITY")}
                      onChange={onResidualQuantityChange}
                      name="center-product-residual_quantity"
                      id="center-product-residual_quantity"
                      value={String(formData.residualQuantity?.value) || ""}
                      isFloat
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.residualQuantity.error}
                      placeholder={t("RESIDUAL_QUANTITY")}
                    />
                    <TextInput
                      type="text"
                      label={t("MIN_BILLING")}
                      onChange={onMinBillingChange}
                      name="center-product-min_billing"
                      id="center-product-min_billing"
                      value={String(formData.minBilling?.value) || ""}
                      isFloat
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.minBilling.error}
                      placeholder={t("MIN_BILLING")}
                    />
                  </GridRow>
                  {isMaterial && (
                    <GridRow column="1fr">
                      <div className="center-product__checkbox_label">
                        <label htmlFor="center-product__checkbox_title">
                          {t("MATERIALS_INFORMATION")}
                        </label>
                        <MaterialUsage
                          materialList={materialUsageList}
                          selectedMaterials={
                            formData.materialUsages.value !== ""
                              ? formData.materialUsages.value.split(",")
                              : []
                          }
                          error={formData.materialUsages.error}
                          setFormData={setFormData}
                          formData={formData}
                        />
                      </div>
                    </GridRow>
                  )}
                  {((formData.subCategory.value !== "" &&
                    (actionMode === "edit" || actionMode === "clone")) ||
                    actionMode === "add") && (
                    <GridRow column="1fr 1fr">
                      <div className="center-product__title_label">
                        <label htmlFor="center-product">
                          {t("ACCEPTANCE_REQUIREMENTS")}
                        </label>
                        <CKEditorText
                          descriptionData={acceptanceRequirements}
                          setDescriptionData={setAcceptanceRequirements}
                        />
                        {formData.acceptanceRequirements.error && (
                          <RequiredMessage>
                            {formData.acceptanceRequirements.error}
                          </RequiredMessage>
                        )}
                      </div>
                      <div className="center-product__title_label">
                        <label htmlFor="center-product">
                          {t("ADDITIONAL_INFORMATION")}
                        </label>
                        <CKEditorText
                          descriptionData={comment}
                          setDescriptionData={setComment}
                        />
                        {formData.comment.error && (
                          <RequiredMessage>
                            {formData.comment.error}
                          </RequiredMessage>
                        )}
                      </div>
                    </GridRow>
                  )}
                </div>
              </SectionContainer>
            </FlexRow>
          </OuterWrapper>
        </FormBody>
        <ButtonWrapper>
          {createCenterProductAPIError && (
            <div>{createCenterProductAPIError}</div>
          )}
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onCancel}
          >
            <>{t("CANCEL")}</>
          </Button>

          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isCenterProductCreating && <ClipLoader size={15} color="#fff" />}
              {!isCenterProductCreating && t("SUBMIT")}
            </>
          </Button>
        </ButtonWrapper>
      </PaddingDiv>
    </OuterWrapperBackground>
  );
};
AddEditCenterProduct.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  isEditMode: PropTypes.bool,
};
AddEditCenterProduct.defaultProps = {
  id: null,
  isEditMode: false,
};

export default withTranslation()(AddEditCenterProduct);
